import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { BookabilityDecision } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { useDefinedWorker } from "../../../../Worker/useDefinedWorker";
import { DialogFooter } from "../../../components/DialogFooter";
import { LoadingButton } from "../../../components/LoadingButton";
import { SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { performChecksBeforeBooking, PreBookingCheck } from "../../Booking/useBookShift";
import { useCheckPreBookingState } from "../../Booking/useCheckPreBookingState";
import { getBackgroundCheckContext } from "../../getBackgroundCheckContext";
import { type ModalShiftData } from "../../types";
import { useBookDoubleShifts } from "../hooks/useBookDoubleShifts";
import type { DoubleShiftPay, ShiftDouble } from "../types";

interface ShiftDoubleBookingFooterProps {
  shift: ModalShiftData;
  double: ShiftDouble;
  selectedCompanion: OpenShift;
  selectedCompanionPay?: DoubleShiftPay;
  hideTotalPay?: boolean;
}

function getDisplayableErrorMessage(error: unknown): string {
  const axiosError = error as {
    response?: { data?: { displayableMessage?: string } };
  };
  return (
    axiosError?.response?.data?.displayableMessage ??
    "Something went wrong while booking these shifts. Please try again."
  );
}

export function ShiftDoubleBookingFooter(props: ShiftDoubleBookingFooterProps) {
  const { shift, double, selectedCompanion, selectedCompanionPay, hideTotalPay = false } = props;

  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    navigateToModal,
    setInitialShiftModalsData,
    bookabilityStatus,
    companionBookabilityStatuses,
  } = useShiftModalsDataContext();

  const { missingDataTypes: missingBackgroundCheckDataTypes } =
    getBackgroundCheckContext(bookabilityStatus);

  const { geolocationIsUnavailable, deviceGeolocation, isPreBookingChecksLoading } =
    useCheckPreBookingState({
      workplaceId: shift.workplace.id,
      shiftId: shift.id,
    });

  const worker = useDefinedWorker();

  const { bookDoubleShifts, isBooking } = useBookDoubleShifts();

  const anchorOfferId = shift.offer?.id;
  const companionOfferId = selectedCompanion.relationships.offer?.data.id;

  const canBook = isDefined(anchorOfferId) && isDefined(companionOfferId);

  const anchorIsBookable =
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.ALLOWED;
  const companionBookability = companionBookabilityStatuses?.get(selectedCompanion.id);
  const companionIsBookable =
    companionBookability?.attributes.bookability.decision === BookabilityDecision.ALLOWED;
  // Only allow booking when both anchor and companion shifts have no blockers
  const allShiftsBookable = anchorIsBookable && companionIsBookable;

  const anchorPay = double.anchorShiftPay?.totalPayInMinorUnits;
  const companionPay = selectedCompanionPay?.totalPayInMinorUnits;
  const totalPay =
    isDefined(anchorPay) && isDefined(companionPay) ? anchorPay + companionPay : undefined;

  const handleBook = async () => {
    if (!canBook) {
      return;
    }

    setIsProcessing(true);

    try {
      const shouldProceed = performChecksBeforeBooking({
        missingBackgroundCheckDataTypes,
        isBasketSizeWarningAcknowledgmentNeeded: false,
        geolocationIsUnavailable,
        requiresBreakPolicyAcknowledgement: false,
        navigateToModal,
        shiftId: shift.id,
        skipChecks: [
          PreBookingCheck.BOOKING_CONFIRMATION,
          PreBookingCheck.BASKET_SIZE_WILL_REACH_WARNING,
          PreBookingCheck.BREAK_POLICY_ACKNOWLEDGEMENT,
        ],
        shouldEducateNewWorker: false,
        shouldShowArrivalTimeModal: false,
        shouldShowClipboardScoreEducation: false,
        history,
      });

      if (!shouldProceed) {
        setIsProcessing(false);
        return;
      }

      const shiftsToBook = [
        { shiftId: shift.id, offerId: anchorOfferId },
        { shiftId: selectedCompanion.id, offerId: companionOfferId },
      ];

      const results = await bookDoubleShifts(shiftsToBook, deviceGeolocation?.geoLocation);
      const failedShiftIds = results.failedShifts.map((f) => f.shiftId);

      logEvent(APP_V2_USER_EVENTS.SHIFT_DOUBLE_BOOKED, {
        workplaceId: double.workplaceId,
        workerId: worker.userId,
        anchorShiftId: shift.id,
        companionShiftId: selectedCompanion.id,
        qualification: double.qualification,
        bookedShiftIds: results.bookedShiftIds,
        failedShiftIds,
      });

      if (results.bookedShiftIds.length === 0) {
        const errorMessage =
          results.failedShifts.length > 0
            ? getDisplayableErrorMessage(results.failedShifts[0].error)
            : "Something went wrong while booking these shifts. Please try again.";
        showErrorToast(errorMessage);
        return;
      }

      if (results.failedShifts.length > 0) {
        showSuccessToast(
          `Booked ${results.bookedShiftIds.length} of 2 shifts. One shift could not be booked.`
        );
      }

      setInitialShiftModalsData({
        shift: {
          ...shift,
          offer: shift.offer,
        },
        double: {
          ...double,
        },
        doubleBookingResults: {
          bookedShiftIds: results.bookedShiftIds,
          failedShiftIds,
        },
      });

      navigateToModal(SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });
    } catch (error: unknown) {
      showErrorToast(getDisplayableErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  if (!allShiftsBookable) {
    return (
      <DialogFooter withShadow orientation="vertical">
        <Stack sx={{ width: "100%", paddingX: 1 }}>
          <Text variant="caption" sx={{ textAlign: "center", color: "text.secondary" }}>
            You're almost there! Tap on each shift to review and complete any open requirements,
            then come back here to book your double.
          </Text>
        </Stack>
      </DialogFooter>
    );
  }

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack sx={{ width: "100%", paddingX: 1, gap: 4 }}>
        {isDefined(totalPay) && !hideTotalPay && (
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Text variant="body2">Total for both shifts</Text>
            <Text semibold variant="h6">
              {formatCentsAsUsd(totalPay)}
            </Text>
          </Stack>
        )}

        <LoadingButton
          fullWidth
          disabled={!canBook || isProcessing || isPreBookingChecksLoading}
          isLoading={isProcessing || isBooking}
          variant="contained"
          size="large"
          onClick={handleBook}
        >
          Book both shifts
        </LoadingButton>
      </Stack>
    </DialogFooter>
  );
}
